import { useCallback, useEffect, useRef, useState } from 'react';
import {
    type BehaviorMapEventHandler,
    type DomEventHandler,
    type LngLat,
    type MapEventUpdateHandler,
    type YMap,
} from '@yandex/ymaps3-types';
import type { YMapCenterLocation, YMapZoomLocation } from '@yandex/ymaps3-types/imperative/YMap';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { useYandexMapsApi } from '@adv-frontend/yandex-maps-kit';
import { GeoLocationControl } from '@/shared/components/GeoLocationControl';
import { ZoomControls } from '@/shared/components/ZoomControls';
import { useGeoLocation } from '@/shared/lib/hooks';
import { DEFAULT_ZOOM, withYandexMapsApiProvider } from '@/shared/lib/yandexMap';
import MarkerIcon from './assets/pin.react.svg';

interface CourierAddressMapProps {
    location: YMapZoomLocation & YMapCenterLocation;
    onChangeLocation: (payload: { geocode: string | LngLat }) => void;
}

export const CourierAddressMap = withYandexMapsApiProvider(function CourierAddressMap(props: CourierAddressMapProps) {
    const { onChangeLocation, location } = props;

    const ymapRef = useRef<null | YMap>(null);
    const yMapsApi = useYandexMapsApi();

    const [coordinates, setCoordinates] = useState<LngLat>();
    const [zoom, setZoom] = useState<number>();
    const [dragging, setDragging] = useState<boolean>(false);

    const onMapClick: DomEventHandler = (_object, event) => {
        setCoordinates(event.coordinates);
        onChangeLocation({ geocode: event.coordinates });
    };

    const onMapUpdate: MapEventUpdateHandler = useCallback(
        (object) => {
            if (dragging) {
                setCoordinates(object.location.center);
            }
        },
        [dragging, setCoordinates]
    );

    const onZoomUpClick = useCallback(() => {
        if (ymapRef.current?.zoom) {
            setZoom(ymapRef.current?.zoom + 1);
            setCoordinates([ymapRef.current?.center[0], ymapRef.current?.center[1]]);
        }
    }, []);

    const onZoomDownClick = useCallback(() => {
        if (ymapRef.current?.zoom) {
            setZoom(ymapRef.current?.zoom - 1);
            setCoordinates([ymapRef.current?.center[0], ymapRef.current?.center[1]]);
        }
    }, []);

    const geoLocation = useGeoLocation();

    const onGeolocatePosition = useCallback(() => {
        if (geoLocation) {
            setCoordinates([geoLocation.longitude, geoLocation.latitude]);
            setZoom(DEFAULT_ZOOM);
        }
    }, [geoLocation]);

    const createBehaviorEventHandler =
        (isStart: boolean): BehaviorMapEventHandler =>
        (object) => {
            if (isStart) {
                if (object.type === 'drag') {
                    setDragging(true);
                }
            } else {
                if (object.type === 'drag') {
                    setCoordinates(object.location.center);
                    onChangeLocation({ geocode: object.location.center });
                    setDragging(false);
                }

                if (object.type === 'scrollZoom' || object.type === 'oneFingerZoom') {
                    setZoom(object.location.zoom);
                }
            }
        };

    useEffect(() => {
        setCoordinates(location.center);
        setZoom(location.zoom);
    }, [location]);

    if (!yMapsApi.maps?.YMap || !yMapsApi.reactify) {
        return null;
    }

    const {
        YMap,
        YMapDefaultSchemeLayer,
        YMapMarker,
        YMapDefaultFeaturesLayer,
        YMapControls,
        YMapControl,
        YMapListener,
    } = yMapsApi.maps;

    return (
        <YMap
            ref={ymapRef}
            location={{
                ...location,
                ...pickBy(
                    {
                        center: coordinates,
                        duration: 200,
                        easing: 'ease-in-out',
                        zoom,
                    },
                    identity
                ),
            }}
            zoomStrategy="zoomToCenter"
        >
            <YMapDefaultSchemeLayer />
            <YMapDefaultFeaturesLayer />
            {coordinates && (
                <YMapMarker coordinates={coordinates} mapFollowsOnDrag>
                    <div style={{ transform: 'translate(-50%, -100%)' }}>
                        <MarkerIcon width="28" />
                    </div>
                </YMapMarker>
            )}
            <YMapControls position="right" orientation="vertical">
                <YMapControl transparent>
                    <ZoomControls onZoomUpClick={onZoomUpClick} onZoomDownClick={onZoomDownClick} />
                </YMapControl>
                {geoLocation && (
                    <YMapControl transparent>
                        <GeoLocationControl onGeolocatePosition={onGeolocatePosition} />
                    </YMapControl>
                )}
            </YMapControls>
            <YMapListener
                onUpdate={onMapUpdate}
                onClick={onMapClick}
                onActionStart={createBehaviorEventHandler(true)}
                onActionEnd={createBehaviorEventHandler(false)}
            />
        </YMap>
    );
});
